/**
 * Client-side supplier ladder progress — mirrors Express
 * `getSupplierLadderProgress` (milestones 3 then 5).
 *
 * Keep constants in sync with express_app/services/growthEngine/config/priorityLadder.js
 */

export const GROWTH_ENGINE_SUPPLIER_FIRST_MILESTONE = 3
export const GROWTH_ENGINE_SUPPLIER_GOAL = 5

/**
 * Same active-supplier rule as Express Growth Engine / Spartacus:
 * isActive && not dismissed.
 */
export function isGrowthEngineActiveSupplier(venueSupplier) {
  return (
    venueSupplier?.isActive === true &&
    venueSupplier?.isSupplierDismissed !== true
  )
}

/**
 * @param {Array<object>|null|undefined} venueSuppliers
 * @returns {number}
 */
export function countGrowthEngineActiveSuppliers(venueSuppliers) {
  if (!Array.isArray(venueSuppliers)) return 0
  return venueSuppliers.filter(isGrowthEngineActiveSupplier).length
}

/**
 * @param {number} activeSupplierCount
 * @returns {{
 *   activeSupplierCount: number,
 *   firstMilestone: number,
 *   goal: number,
 *   status: 'below_milestone'|'below_goal'|'at_goal',
 *   volume: 'loud'|'present'|'quiet',
 *   remainingToMilestone: number,
 *   remainingToGoal: number,
 *   progressTarget: number,
 *   progressRatio: number,
 * }}
 */
export function getSupplierLadderProgress(activeSupplierCount) {
  const count = Math.max(0, Number(activeSupplierCount) || 0)
  const firstMilestone = GROWTH_ENGINE_SUPPLIER_FIRST_MILESTONE
  const goal = GROWTH_ENGINE_SUPPLIER_GOAL

  let status = 'below_milestone'
  let volume = 'loud'
  if (count >= goal) {
    status = 'at_goal'
    volume = 'quiet'
  } else if (count >= firstMilestone) {
    status = 'below_goal'
    volume = 'present'
  }

  const progressTarget = status === 'below_milestone' ? firstMilestone : goal
  const progressRatio = Math.min(1, count / progressTarget)

  return {
    activeSupplierCount: count,
    firstMilestone,
    goal,
    status,
    volume,
    remainingToMilestone: Math.max(0, firstMilestone - count),
    remainingToGoal: Math.max(0, goal - count),
    progressTarget,
    progressRatio,
  }
}

/**
 * Benefit-led copy for the suppliers progress strip (not gamified).
 * Returns null when the strip should not show (at goal).
 *
 * @param {ReturnType<typeof getSupplierLadderProgress>} progress
 * @returns {{ label: string, headline: string, detail: string }|null}
 */
export function getSupplierProgressCopy(progress) {
  if (!progress || progress.status === 'at_goal') {
    return null
  }

  const { activeSupplierCount: n, firstMilestone, goal, status } = progress

  if (status === 'below_milestone') {
    if (n === 0) {
      return {
        label: `0 of ${firstMilestone} to your first milestone`,
        headline: 'Bring your chain into OMS',
        detail:
          'Connect the suppliers you already buy from so ordering stays in one place — less juggling, fewer missed or double orders.',
      }
    }
    if (n === 1) {
      return {
        label: `1 of ${firstMilestone} to your first milestone`,
        headline: 'One supplier is a start — not coverage',
        detail: `Add ${
          firstMilestone - 1
        } more and you’ll see real spend patterns — the foundation for cutting waste and protecting margin.`,
      }
    }
    return {
      label: `${n} of ${firstMilestone} — almost there`,
      headline: 'Nearly at solid coverage',
      detail: `One more active supplier gets you to ${firstMilestone}. That breadth is what makes leftovers, gaps, and savings visible.`,
    }
  }

  // below_goal (3–4)
  return {
    label: `${n} of ${goal} — goal in sight`,
    headline: 'Good coverage — keep going to five',
    detail: `At ${goal} suppliers your purchasing picture is broad enough to trust for par levels, stock takes, and recipes.`,
  }
}

/**
 * Whether the Free-venue suppliers progress strip should render.
 *
 * @param {{ currentPlan?: string|null, activeSupplierCount?: number }} params
 */
export function shouldShowSupplierProgress({
  currentPlan,
  activeSupplierCount,
} = {}) {
  if (currentPlan && currentPlan !== 'Free') return false
  const progress = getSupplierLadderProgress(activeSupplierCount)
  return getSupplierProgressCopy(progress) != null
}
